import { VariableType } from 'json-to-graphql-query';
import { AssetNodeType } from 'src/dynamoQueries/assets';
import type { FieldValue, PageInfoFields } from './types';

interface UserFields {
  id: FieldValue;
  email: FieldValue;
  username: FieldValue;
}

interface ActionFields {
  id: FieldValue;
  title: FieldValue;
  type: FieldValue;
}

interface FormatFields {
  id: FieldValue;
  filename: FieldValue;
  mimetype: FieldValue;
}

interface ItemFields {
  id: FieldValue;
  contentObject: {
    __on: ReturnType<typeof AssetNodeType>;
  };
}

interface JobNodeTypeResult {
  id: FieldValue;
  user: UserFields | FieldValue;
  status: FieldValue;
  statusMsg: FieldValue;
  createdTimestamp: FieldValue;
  modifiedTimestamp: FieldValue;
  action: ActionFields | FieldValue;
  metadata: FieldValue;
  format: FormatFields | FieldValue;
  item: ItemFields | FieldValue;
}

interface JobsQueryResult {
  query: {
    __variables: {
      before: 'String';
      after: 'String';
      first: 'Int';
      last: 'Int';
    };
    allJobs: {
      __args: {
        before: VariableType;
        after: VariableType;
        first: VariableType;
        last: VariableType;
      };
      totalCount: FieldValue;
      pageInfo: PageInfoFields;
      edges: {
        node: {
          id: FieldValue;
          user: UserFields;
          status: FieldValue;
          statusMsg: FieldValue;
          createdTimestamp: FieldValue;
          modifiedTimestamp: FieldValue;
          action: ActionFields;
          metadata: FieldValue;
          format: FormatFields;
          item: ItemFields;
        };
      };
    };
  };
}

interface AllJobsBasicQueryResult {
  query: {
    allJobs: {
      __args: Record<string, never>;
      edges: {
        node: JobNodeTypeResult;
      };
    };
  };
}

interface GetJobResultsMutationResult {
  mutation: {
    __variables: {
      jobId: 'String!';
    };
    getJobResult: {
      __args: {
        jobId: VariableType;
      };
      result: FieldValue;
    };
  };
}

export const jobsQuery = (): JobsQueryResult => ({
  query: {
    __variables: {
      before: 'String',
      after: 'String',
      first: 'Int',
      last: 'Int',
    },
    allJobs: {
      __args: {
        before: new VariableType('before'),
        after: new VariableType('after'),
        first: new VariableType('first'),
        last: new VariableType('last'),
      },
      totalCount: true,
      pageInfo: {
        hasNextPage: true,
        hasPreviousPage: true,
        startCursor: true,
        endCursor: true,
      },
      edges: {
        node: {
          id: true,
          user: {
            id: true,
            email: true,
            username: true,
          },
          status: true,
          statusMsg: true,
          createdTimestamp: true,
          modifiedTimestamp: true,
          action: {
            id: true,
            title: true,
            type: true,
          },
          metadata: true,
          format: {
            id: true,
            filename: true,
            mimetype: true,
          },
          item: {
            id: true,
            contentObject: {
              __on: AssetNodeType(true),
            },
          },
        },
      },
    },
  },
});

export const JobNodeType = (truify: boolean = false): JobNodeTypeResult => ({
  id: truify,
  user: truify,
  status: truify,
  statusMsg: truify,
  createdTimestamp: truify,
  modifiedTimestamp: truify,
  action: truify,
  metadata: truify,
  format: truify,
  item: truify,
});

export const allJobsBasicQuery = (node: JobNodeTypeResult = JobNodeType(true)): AllJobsBasicQueryResult => ({
  query: {
    allJobs: {
      __args: {},
      edges: {
        node,
      },
    },
  },
});

export const getJobResults = (): GetJobResultsMutationResult => ({
  mutation: {
    __variables: {
      jobId: 'String!',
    },
    getJobResult: {
      __args: {
        jobId: new VariableType('jobId'),
      },
      result: true,
    },
  },
});
